import type { OperationResult } from '@/common/errors/OperationResult'
import * as op from '../../common/errors/OperationResult'
import { BehaviorSubject, Observable, Subject, switchMap, tap } from 'rxjs'
import type { IBehaviorObservable } from '@/common/react/IBehaviorObservable'
import type { CreateObjectTypeInput } from '../gql/CreateObjectTypeInput'
import type { CreatePropertyDefinitionInput } from '../gql/CreatePropertyDefinitionInput'
import type { ObjectTypesApiService } from './ObjectTypesApiService'
import type { SpacesService } from '@/spaces/services/SpacesService'
import { logError } from '@/common/errors/log-utils'
import {
  CreatePropertyOptionInput,
  type LocalUpdatePropertyDefinitionInput,
  type LocalUpdatePropertyOptionInput,
  localUpdPropDefInputToRemote,
} from '../gql/UpdatePropertyDefinitionInput'
import type { PropertyOptionsService } from './PropertyOptionService'
import { createTmpClientId, isClientId } from '@/common/network/gql-types'
import { removeBy, replace, replaced, replacedAt } from '@/common/collection-utils'
import type { PropertyDefinitionModel, ObjectTypeModel } from '@/core/gql/gql-models-override.ts'
import type { PropertyOptionModel } from '@/generated/graphql.ts'
import { assertIsNumber } from '@/common/type-utils.ts'
import type { PropertyDefinition } from '@/object-types/gql/PropertyDefinition'
import type { BatchUpdateTypeInput } from '@/object-types/gql/BatchUpdateTypeInput'
import { AppError } from '@/common/errors/app-errors.ts'

export enum TypeChangeKind {
  TypeAdded = 0,
  TypeRenamed = 1,
  TypeDeleted = 2,
  PropDefAdded = 3,
  PropDefUpdated = 4,
  PropDefDeleted = 5,
}

export type TypeChangeEvent =
  | { kind: TypeChangeKind.TypeAdded; typeId: number; type: ObjectTypeModel }
  | { kind: TypeChangeKind.TypeRenamed; typeId: number; name: string }
  | { kind: TypeChangeKind.TypeDeleted; typeId: number }
  | { kind: TypeChangeKind.PropDefAdded; typeId: number; propDef: PropertyDefinitionModel }
  | { kind: TypeChangeKind.PropDefUpdated; typeId: number; propDef: PropertyDefinitionModel }
  | { kind: TypeChangeKind.PropDefDeleted; typeId: number; propDefId: number }

export const corePropDefs = [
  { name: 'Name', type: 'text' },
  { name: 'Description', type: 'text' },
] as PropertyDefinition[]

export function isCorePropDef(propDef: PropertyDefinitionModel) {
  return corePropDefs.some((x) => x.name === propDef.name)
}

export class ObjectTypesService {
  public typeChanged$: Observable<TypeChangeEvent[]>
  public selectedSpaceTypes$: IBehaviorObservable<ObjectTypeModel[] | undefined>
  public idToPropertyDef: Map<number, PropertyDefinitionModel> = new Map()
  private readonly typeChanged: Subject<TypeChangeEvent[]>
  private readonly selectedSpaceTypes: BehaviorSubject<ObjectTypeModel[] | undefined>
  private fetchAbortController?: AbortController

  constructor(
    private readonly spacesService: SpacesService,
    private readonly apiService: ObjectTypesApiService,
    private readonly optionsService: PropertyOptionsService,
  ) {
    this.typeChanged = new Subject<TypeChangeEvent[]>()
    this.typeChanged$ = this.typeChanged.asObservable()
    this.selectedSpaceTypes = new BehaviorSubject<ObjectTypeModel[] | undefined>(undefined)
    this.selectedSpaceTypes$ = this.selectedSpaceTypes
    this.syncTypesForSelectedSpace()
  }

  public getTypeFromSelectedSpace(typeId: number): ObjectTypeModel | undefined {
    const types = this.selectedSpaceTypes$.value
    if (!types) {
      return undefined
    }

    return types.find((x) => x.id === typeId)
  }

  public async createType(input: CreateObjectTypeInput): Promise<OperationResult<ObjectTypeModel>> {
    const { error, result: createdType } = await this.apiService.createType(input)

    if (error) {
      return op.error(error)
    }

    const types = [...this.selectedSpaceTypes.value!, createdType!]
    this.selectedSpaceTypes.next(types)
    this.typeChanged.next([
      {
        kind: TypeChangeKind.TypeAdded,
        typeId: createdType!.id,
        type: createdType!,
      },
    ])
    return op.success(createdType!)
  }

  public async renameType(id: number, name: string): Promise<OperationResult> {
    const types = this.selectedSpaceTypes.value!
    const idx = types.findIndex((t) => t.id === id)

    if (idx === -1) {
      logError(`renaming type - type with id ${id} not found`)
      return op.error(new AppError())
    }

    const { error, result: updatedType } = await this.apiService.renameType(id, name)
    if (error) {
      return op.error(error)
    }
    this.selectedSpaceTypes.next(replacedAt(types, idx, updatedType!))
    this.typeChanged.next([{ kind: TypeChangeKind.TypeRenamed, typeId: id, name }])
    return op.success()
  }

  public async deleteType(id: number): Promise<OperationResult> {
    const { error } = await this.apiService.deleteType(id)
    if (error) {
      return op.error(error)
    }
    const types = this.selectedSpaceTypes.value!.filter((t) => t.id !== id)
    this.selectedSpaceTypes.next(types)
    this.typeChanged.next([{ kind: TypeChangeKind.TypeDeleted, typeId: id }])
    return op.success()
  }

  public async batchUpdateType(input: BatchUpdateTypeInput): Promise<OperationResult> {
    const { error, result } = await this.apiService.batchUpdateType(input)
    if (error) return op.error(error)

    const { updatedType, updatedPropDefs } = result!
    const typeId = input.typeId
    const types = this.selectedSpaceTypes.value!
    const idx = types.findIndex((t) => t.id === typeId)

    if (idx !== -1) {
      let finalType: ObjectTypeModel
      if (updatedType) {
        finalType = updatedType
      } else {
        const propDefs = [...types[idx].propertyDefinitions]
        for (const upd of updatedPropDefs) {
          const pi = propDefs.findIndex((p) => p.id === upd.id)
          if (pi !== -1) propDefs[pi] = upd
        }
        finalType = { ...types[idx], propertyDefinitions: propDefs }
      }
      this.selectedSpaceTypes.next(replacedAt(types, idx, finalType))
    }

    const events: TypeChangeEvent[] = []
    if (input.typeInput?.name)
      events.push({ kind: TypeChangeKind.TypeRenamed, typeId, name: input.typeInput.name })
    if (updatedType) {
      for (const propDef of updatedType.propertyDefinitions.filter((p) =>
        input.typeInput?.propertyCreateInputs?.some((c) => c.name === p.name),
      ))
        events.push({ kind: TypeChangeKind.PropDefAdded, typeId, propDef })
    }
    for (const propDefId of input.typeInput?.propertiesToDelete ?? [])
      events.push({ kind: TypeChangeKind.PropDefDeleted, typeId, propDefId })
    if (events.length) this.typeChanged.next(events)

    return op.success()
  }

  public async updatePropertyDef(
    propDef: PropertyDefinitionModel,
    input: LocalUpdatePropertyDefinitionInput,
  ): Promise<OperationResult> {
    const updatedPropDef = this.updatePropDefCopy(propDef, input)
    const type = this.getTypeFromSelectedSpace(propDef.objectTypeId)!
    type.propertyDefinitions = replaced(type.propertyDefinitions, propDef, updatedPropDef)
    this.selectedSpaceTypes.next([...this.selectedSpaceTypes.value!])
    this.typeChanged.next([
      {
        kind: TypeChangeKind.PropDefUpdated,
        typeId: type.id,
        propDef: updatedPropDef,
      },
    ])
    if (isClientId(propDef.id)) {
      return op.success()
    }
    const { result: remotePropDef, error } = await this.apiService.updatePropertyDef(
      localUpdPropDefInputToRemote(input, propDef.id),
    )
    if (error) {
      return op.error(error)
    }
    if (input.options?.locallyCreatedOptions) {
      this.reconcileOptions(updatedPropDef, remotePropDef!)
    }
    return op.success()
  }

  public async addPropertyDef(
    typeId: number,
    input: CreatePropertyDefinitionInput,
  ): Promise<OperationResult<PropertyDefinitionModel>> {
    const newPropDef: PropertyDefinitionModel = {
      id: createTmpClientId(),
      name: input.name,
      type: input.type,
      isMeta: false,
      objectTypeId: typeId,
      options: null,
    }
    const type = this.getTypeFromSelectedSpace(typeId)!
    type.propertyDefinitions.push(newPropDef)
    this.selectedSpaceTypes.next([...this.selectedSpaceTypes.value!])

    const { error, result: remotePropDef } = await this.apiService.createPropertyDef({
      ...input,
      objectTypeId: typeId,
    })

    if (error) {
      return op.error(`error creating property def: ${error.message}, typeId: ${typeId}`)
    }
    replace(type.propertyDefinitions, newPropDef, remotePropDef!)
    this.selectedSpaceTypes.next([...this.selectedSpaceTypes.value!])
    this.typeChanged.next([
      {
        kind: TypeChangeKind.PropDefAdded,
        typeId: type.id,
        propDef: remotePropDef!,
      },
    ])

    return op.success(newPropDef)
  }

  public deletePropertyDef(propDef: PropertyDefinitionModel): OperationResult {
    const type = this.getTypeFromSelectedSpace(propDef.objectTypeId)!
    removeBy(type.propertyDefinitions, (x) => x.id === propDef.id)
    this.selectedSpaceTypes.next([...this.selectedSpaceTypes.value!])
    this.typeChanged.next([
      {
        kind: TypeChangeKind.PropDefDeleted,
        typeId: type.id,
        propDefId: propDef.id,
      },
    ])
    return op.success()
  }

  public createLocalPropertyOption(
    propDef: PropertyDefinitionModel,
    input: CreatePropertyOptionInput,
  ): PropertyOptionModel {
    const color = this.optionsService.nextDefaultColor(propDef.options!.length)
    return {
      id: createTmpClientId(),
      value: input.value,
      color,
    }
  }

  private syncTypesForSelectedSpace(): void {
    this.spacesService.selectedSpace$
      .pipe(
        switchMap(async (space) => {
          if (!space) {
            return undefined
          }
          this.fetchAbortController?.abort()
          this.fetchAbortController = new AbortController()
          return await this.fetchTypes(space.id)
        }),
        tap((types) => {
          this.idToPropertyDef = this.buildIdToPropertyDef(types ?? [])
          this.selectedSpaceTypes.next(types)
        }),
      )
      .subscribe()
  }

  private updatePropDefCopy(
    propDef: PropertyDefinitionModel,
    updInput: LocalUpdatePropertyDefinitionInput,
  ): PropertyDefinitionModel {
    const updated = { ...propDef, ...updInput } as PropertyDefinitionModel
    if (updInput.options) {
      const locallyCreatedOptions = updInput.options.locallyCreatedOptions

      const newOptions = locallyCreatedOptions ? [...locallyCreatedOptions] : []
      for (const currOption of propDef.options!) {
        if (updInput.options.toDelete && updInput.options.toDelete.some((x) => x == option.id)) {
          continue
        }

        const upd = updInput.options.updates?.find((x) => x.id == currOption.id)
        let option = currOption
        if (upd) {
          option = this.applyOptionUpdate(currOption, upd)
        }

        newOptions.push(option)
      }

      updated.options = newOptions
    }
    return updated
  }

  private async fetchTypes(spaceId: number): Promise<ObjectTypeModel[] | undefined> {
    const { error, result: types } = await this.apiService.fetchTypes(
      spaceId,
      this.fetchAbortController!.signal,
    )
    if (error) {
      logError(`error fetching types for space: ${error.message}`, { spaceId })
      return undefined
    }

    console.log(`types fetched: ${types!.length}`)
    return types
  }

  private applyOptionUpdate(
    currOption: PropertyOptionModel,
    update: LocalUpdatePropertyOptionInput,
  ): PropertyOptionModel {
    return { ...currOption, ...update }
  }

  private buildIdToPropertyDef(types: ObjectTypeModel[]): Map<number, PropertyDefinitionModel> {
    const map = new Map<number, PropertyDefinitionModel>()
    for (const type of types) {
      for (const def of type.propertyDefinitions) {
        assertIsNumber(def.id)
        map.set(def.id, def)
      }
    }
    return map
  }

  private reconcileOptions(
    propDef: PropertyDefinitionModel,
    remotePropDef: PropertyDefinitionModel,
  ) {
    removeBy(propDef.options!, (x) => isClientId(x.id))
    for (const serverOption of remotePropDef.options!) {
      const exists = propDef.options!.some((x) => x.id === serverOption.id)
      if (!exists) {
        propDef.options!.push(serverOption)
      }
    }
  }
}
